import React from "react";
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Help as HelpIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import PageContainer from "../../components/PageContainer";
import PageSection from "../../components/PageSection";
import SignupProcessInfographic from "../../components/SignupProcessInfographic";
import { NoticeboardButton } from "../../components/shared";
import { lightTheme } from "../../styles/theme";

const LoginAuthHelp: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const faqItems = [
    {
      question: "How long does signup take?",
      answer:
        "Most organizations are approved within 24 hours. After you sign up, verify your email, log in, and our team reviews your organization before you get full access.",
    },
    {
      question: "What if I don't receive the verification code?",
      answer:
        "Check your spam folder first. If it's not there, press Resend verification link on the Verify Your Account screen. Make sure you use the same email address you signed up with.",
    },
    {
      question: "Why is my account still pending approval?",
      answer:
        "Every new organization is reviewed by our team to keep Noticeboard safe. While you wait, you can finish your profile and watch the video tutorials.",
    },
    {
      question: "I'm an Admin and forgot my password. What do I do?",
      answer:
        "On the login page, choose Organization Admin and press Forgot Password?. Enter your email to get a reset code, then enter the code and a new password of at least 8 characters.",
    },
    {
      question: "I'm a Team member and forgot my PIN. What do I do?",
      answer:
        "Team members log in with their username or phone number and a 4-digit PIN. If you've forgotten your PIN, ask your organization's Admin to reset it.",
    },
    {
      question: "Can more than one person use the dashboard?",
      answer:
        "Yes. Once approved, Admins and Owners can add Team members from Team with ADD MEMBER, as an Admin, Owner or Creator.",
    },
  ];

  const securityTips = [
    "Use a strong password that you don't use anywhere else",
    "Never share your password or PIN, even with colleagues",
    "Log out when using a shared computer",
    "Remove Team members who have left your organization",
    "Tell your organization's Admin straight away if you notice anything suspicious",
  ];

  return (
    <PageContainer>
      {/* Header */}
      <PageSection sx={{ mb: 4 }}>

        <Paper
          elevation={0}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            borderRadius: 3,
            p: { xs: 4, md: 6 },
            color: "common.white",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            Login & Authentication
          </Typography>
          <Typography
            variant="h6"
            sx={{
              opacity: 0.9,
              fontSize: { xs: "1rem", md: "1.125rem" },
              maxWidth: "800px",
              mx: "auto",
              lineHeight: 1.6,
              fontWeight: 400,
            }}
          >
            Everything you need to know about signing up, logging in, and
            keeping your account secure
          </Typography>
        </Paper>
      </PageSection>

      {/* Signup Process Infographic */}
      <PageSection sx={{ mb: 6 }}>
        <SignupProcessInfographic />
      </PageSection>

      {/* Security Tips */}
      <PageSection sx={{ mb: 6 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            bgcolor: "card",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <SecurityIcon sx={{ fontSize: 32, color: "primary.main", mr: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Security Best Practices
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
            Follow these security tips to keep your organization account safe
            and secure:
          </Typography>

          <List>
            {securityTips.map((tip, index) => (
              <ListItem key={index} sx={{ py: 1, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircleIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={tip}
                  sx={{ "& .MuiListItemText-primary": { fontSize: "1rem" } }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      </PageSection>

      {/* FAQ Section */}
      <PageSection sx={{ mb: 6 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            bgcolor: "card",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <HelpIcon sx={{ fontSize: 32, color: "primary.main", mr: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Frequently Asked Questions
            </Typography>
          </Box>

          {faqItems.map((item, index) => (
            <Accordion
              key={index}
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                mb: 2,
                "&:before": { display: "none" },
                "&:last-child": { mb: 0 },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  "& .MuiAccordionSummary-content": {
                    my: 2,
                  },
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {item.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 3 }}>
                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                  {item.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      </PageSection>

      {/* Contact Support */}
      <PageSection>
        <Paper
          elevation={0}
          sx={{
            background: `linear-gradient(135deg, ${lightTheme.colors.gradientLight[0]} 0%, ${lightTheme.colors.gradientLight[1]} 100%)`,
            borderRadius: 3,
            p: 4,
            textAlign: "center",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Still Need Help?
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
            Team members who can't log in should ask their organization's
            Admin. For everything else, our video tutorials walk you through
            the most common tasks.
          </Typography>
          <NoticeboardButton
            variant="primary"
            size="large"
            onClick={() => navigate("/help/videos")}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: "1.1rem",
              fontWeight: 600,
            }}
          >
            Browse Tutorials
          </NoticeboardButton>
        </Paper>
      </PageSection>
    </PageContainer>
  );
};

export default LoginAuthHelp;
